/* ==========================================================================
   Product gallery — thumbnail strip
   Vanilla JS, no dependencies. Keeps the active thumbnail in sync and scrolls
   it into view inside the strip. Swapping the main image is still done by
   timber.js (and elevateZoom on desktop) until the zoom/popup rebuild.
   ========================================================================== */
(function () {
  'use strict';

  var STRIP_ID = 'ProductThumbs';
  var THUMB = '.pg-thumb';
  var ACTIVE = 'active';

  function getStrip() {
    return document.getElementById(STRIP_ID);
  }

  // Scroll only the strip, horizontally, so the thumb ends up centered.
  // (scrollIntoView would also scroll the whole page vertically.)
  function scrollIntoStrip(strip, thumb, smooth) {
    var s = strip.getBoundingClientRect();
    var t = thumb.getBoundingClientRect();
    if (t.left >= s.left && t.right <= s.right) return;
    strip.scrollBy({
      left: t.left - s.left - (s.width - t.width) / 2,
      behavior: smooth ? 'smooth' : 'auto'
    });
  }

  function setActive(thumb, smooth) {
    var strip = getStrip();
    if (!strip || !thumb) return;
    var thumbs = strip.querySelectorAll(THUMB);
    for (var i = 0; i < thumbs.length; i++) {
      thumbs[i].classList.toggle(ACTIVE, thumbs[i] === thumb);
    }
    scrollIntoStrip(strip, thumb, smooth);
  }

  // Public: mark the thumbnail for a given Shopify image id as active.
  function select(imageId) {
    var strip = getStrip();
    if (!strip) return;
    setActive(strip.querySelector(THUMB + '[data-image-id="' + imageId + '"]'), true);
  }

  // Delegated so it survives the section being re-rendered in the theme editor.
  document.addEventListener('click', function (e) {
    var thumb = e.target.closest && e.target.closest('#' + STRIP_ID + ' ' + THUMB);
    if (thumb) setActive(thumb, true);
  });

  // Toggle state classes from the scroll position (reads only, never sets sizes):
  //  - wrapper.has-more: right-edge fade while there are more thumbs to the right
  //  - wrapper.has-more-left: left-edge fade while thumbs are hidden to the left
  //  - strip.is-scrollable: there is overflow at all (enables grab cursor/drag)
  function updateScrollState() {
    var strip = getStrip();
    if (!strip || !strip.parentNode) return;
    var more = strip.scrollLeft + strip.clientWidth < strip.scrollWidth - 1;
    strip.parentNode.classList.toggle('has-more', more);
    strip.parentNode.classList.toggle('has-more-left', strip.scrollLeft > 1);
    strip.classList.toggle('is-scrollable', canScroll(strip));
  }

  // 'scroll' doesn't bubble: listen in capture phase so it survives re-renders.
  document.addEventListener('scroll', function (e) {
    if (e.target && e.target.id === STRIP_ID) updateScrollState();
  }, true);
  window.addEventListener('resize', updateScrollState);

  // --- Desktop mouse navigation: wheel + click-and-drag ----------------------
  // Only with a real mouse/trackpad; touch keeps the native swipe untouched.
  var finePointer = window.matchMedia
    ? window.matchMedia('(hover: hover) and (pointer: fine)')
    : { matches: false };
  var DRAG_THRESHOLD = 5; // px of horizontal movement before it counts as a drag
  var drag = null;        // { strip, startX, startScroll, moved }
  var suppressClick = false;

  function canScroll(strip) {
    return strip.scrollWidth > strip.clientWidth + 1;
  }

  // Vertical wheel -> horizontal scroll of the strip.
  function onWheel(e) {
    var strip = e.currentTarget;
    if (!finePointer.matches || e.ctrlKey || !canScroll(strip)) return;
    // Trackpad sideways swipes and Shift+wheel already scroll horizontally
    if (Math.abs(e.deltaX) >= Math.abs(e.deltaY)) return;
    var delta = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY; // Firefox: lines
    var max = strip.scrollWidth - strip.clientWidth;
    // At either end, let the page scroll instead of trapping the wheel
    if ((delta < 0 && strip.scrollLeft <= 0) || (delta > 0 && strip.scrollLeft >= max - 1)) return;
    e.preventDefault();
    strip.scrollLeft += delta;
  }

  // Bound on the strip itself (not document) so the page's own wheel scrolling
  // stays passive. The attribute avoids double-binding on re-init.
  function bindStrip() {
    var strip = getStrip();
    if (!strip || strip.hasAttribute('data-pg-bound')) return;
    strip.setAttribute('data-pg-bound', '');
    strip.addEventListener('wheel', onWheel, { passive: false });
  }

  document.addEventListener('mousedown', function (e) {
    if (e.button !== 0 || !finePointer.matches) return;
    var strip = e.target.closest && e.target.closest('#' + STRIP_ID);
    if (!strip || !canScroll(strip)) return;
    drag = { strip: strip, startX: e.clientX, startScroll: strip.scrollLeft, moved: false };
  });

  document.addEventListener('mousemove', function (e) {
    if (!drag) return;
    // Button released outside the browser window: the mouseup never arrived
    if (!(e.buttons & 1)) { endDrag(); return; }
    var dx = e.clientX - drag.startX;
    if (!drag.moved) {
      if (Math.abs(dx) <= DRAG_THRESHOLD) return;
      drag.moved = true;
      drag.strip.classList.add('is-dragging');
    }
    e.preventDefault();
    drag.strip.scrollLeft = drag.startScroll - dx;
  });

  function endDrag() {
    if (!drag) return;
    if (drag.moved) {
      drag.strip.classList.remove('is-dragging');
      // The click the browser fires right after this mouseup must not select
      // a thumbnail. Reset on the next tick in case no click follows (e.g. the
      // mouse was released outside the strip).
      suppressClick = true;
      setTimeout(function () { suppressClick = false; }, 0);
    }
    drag = null;
  }
  document.addEventListener('mouseup', endDrag);

  // Capture phase on document runs before timber.js, elevateZoom and our own
  // click handler, so a drag never selects a thumbnail or follows its href.
  document.addEventListener('click', function (e) {
    if (!suppressClick) return;
    suppressClick = false;
    e.preventDefault();
    e.stopImmediatePropagation();
  }, true);

  // Links and images are natively draggable (ghost image); disable that here.
  document.addEventListener('dragstart', function (e) {
    if (e.target.closest && e.target.closest('#' + STRIP_ID)) e.preventDefault();
  });

  // On load, bring the initially active thumb into view without animating
  // (e.g. a variant URL whose image is the 8th thumbnail).
  function init() {
    var strip = getStrip();
    if (strip) setActive(strip.querySelector(THUMB + '.' + ACTIVE), false);
    bindStrip();
    updateScrollState();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor re-renders the section: re-bind the wheel on the new strip.
  document.addEventListener('shopify:section:load', init);

  window.ProductGallery = { select: select };
})();
